import React from "react"
import { FormGroup, Label, FormFeedback } from "reactstrap"
import Select from "react-select"

const SelectInput = ({
  label,
  name,
  placeholder,
  validation,
  options = [],
  helperText,
  readOnly = false,
  disabled = false,
  required = true,
  className = "",
  emptyText, // Custom empty text, defaults to "[label] not found"
  isClearable = true,
  isMulti = false,
  onSelectionChange,
  capitalizeReadOnly = false,
  ...rest
}) => {
  const showHelperText = !readOnly && !disabled && helperText
  const hasError = validation.touched[name] && validation.errors[name]
  const hasValue = validation.values[name]

  // Check if the field is required
  const isRequired = required && !readOnly && !disabled

  // Get display value for readonly mode
  const getDisplayValue = () => {
    if (readOnly && !hasValue) {
      const fieldName = label ? label.replace("*", "").trim() : name
      return emptyText || `${fieldName} not found`
    }
    if (readOnly && hasValue) {
      if (isMulti && Array.isArray(hasValue)) {
        const result = hasValue.map(item => {
          // If item is an object with label/value, use it
          if (typeof item === 'object' && item !== null) {
            return item.label || item.value
          }
          // If item is a string, try to find the corresponding option
          if (typeof item === 'string') {
            const foundOption = options.find(opt => opt.value === item)
            return foundOption ? foundOption.label : item
          }
          return item
        }).join(", ")
        return result
      }
      return hasValue.label || hasValue.value || hasValue
    }
    return ""
  }

  // Select styles – layout-specific only; theming handled by _react-select.scss
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "calc(1.5em + 0.75rem + 2px)", // Match TextInput height
    }),
    // Make dropdown menu render above everything
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  // Handle change
  const handleChange = (selectedOption) => {
    // Always store the full option object for the Select component to display correctly
    validation.setFieldValue(name, selectedOption)
    if (onSelectionChange) {
      onSelectionChange(selectedOption)
    }
  }

  // Get the display value for the Select component
  const getSelectValue = () => {
    const currentValue = validation.values[name]
    // If the value is a string (manually set by onSelectionChange), 
    // find the matching option to display
    if (typeof currentValue === 'string' && currentValue) {
      return options.find(opt => opt.value === currentValue) || null
    }

    // If it's an object (default behavior), return as-is
    return currentValue || null
  }

  // If readonly, show as text instead of select (match TextInput exactly)
  if (readOnly) {
    return (
      <FormGroup className={`mb-3 ${className}`}>
        {label && (
          <Label htmlFor={name}>
            {label}
          </Label>
        )}
        <div
          className={`form-control input-as-text ${!hasValue ? "text-danger" : ""} ${capitalizeReadOnly ? "text-capitalize" : ""}`}
        >
          {getDisplayValue()}
        </div>
      </FormGroup>
    )
  }

  return (
    <FormGroup className={`mb-3 ${className}`}>
      {label && (
        <Label htmlFor={name} className={disabled ? "text-muted" : ""}>
          {label}
          {isRequired ? " *" : ""}
        </Label>
      )}

      <Select
        inputId={name}
        name={name}
        placeholder={placeholder}
        options={options}
        value={getSelectValue()}
        onChange={handleChange}
        onBlur={() => validation.setFieldTouched(name, true)}
        isDisabled={readOnly || disabled}
        isClearable={isClearable}
        isMulti={isMulti}
        styles={selectStyles}
        className={`select2-selection ${hasError ? "is-invalid" : ""}`}
        classNamePrefix="select"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        {...rest}
      />

      {hasError ? (
        <FormFeedback type="invalid" className="d-block">
          {validation.errors[name]}
        </FormFeedback>
      ) : null}

      {showHelperText && (
        <span className="text-muted text-end d-inline-block w-100">
          {helperText}
        </span>
      )}
    </FormGroup>
  )
}

export default SelectInput